import { usePageHeader } from "contexts/PageHeaderContext";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import { TextareaUI } from "ui/textareaUI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Switch } from "ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "ui/tabs";
import {
  Clock,
  Plus,
  Search,
  Edit2,
  Trash2,
  Check,
  Hotel,
  Plane,
  MapPin,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  getBufferConfigurations,
  getPorts,
  getVenues,
  createBufferConfiguration,
  toggleBufferStatus,
  updateBufferConfiguration,
  deleteBufferConfiguration,
} from "services/bufferConfigurationService";
import { useScreenPermission } from "hooks/useScreenPermission";
import { TimePickerField } from "ui/bufferTimePickerField";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";

// Types
interface AccommodationBuffer {
  id: string;
  venue: string;
  venueId: string;
  checkInBuffer: string; // time in HH:MM format
  checkOutBuffer: string; // time in HH:MM format
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TransportBuffer {
  id: string;
  venue: string;
  venueId: string;
  port: string;
  portName: string;
  preFlightBuffer: string; // time in HH:MM format
  postFlightBuffer: string; // time in HH:MM format
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const BUFFER_TYPE = {
  ACCOMMODATION: 1,
  TRANSPORT: 2,
};

type SortFieldAccommodation = "venue" | "checkInBuffer" | "checkOutBuffer";
type SortFieldTransport = "venue" | "preFlightBuffer" | "postFlightBuffer";
type SortOrder = "asc" | "desc" | null;
export const BufferConfigurationMaster = () => {

  const [activeTab, setActiveTab] = useState("accommodation");

  // Accommodation state
  const [accommodationBuffers, setAccommodationBuffers] = useState<
    AccommodationBuffer[]
  >([]);
  const [searchTermAccommodation, setSearchTermAccommodation] = useState("");
  const [showDialogAccommodation, setShowDialogAccommodation] = useState(false);
  const [editingAccommodation, setEditingAccommodation] =
    useState<AccommodationBuffer | null>(null);
  const [deleteConfirmAccommodation, setDeleteConfirmAccommodation] = useState<
    string | null
  >(null);
  const [sortFieldAccommodation, setSortFieldAccommodation] =
    useState<SortFieldAccommodation | null>(null);
  const [sortOrderAccommodation, setSortOrderAccommodation] =
    useState<SortOrder>(null);

  // Transport state
  const [transportBuffers, setTransportBuffers] = useState<TransportBuffer[]>(
    [],
  );
  const [searchTermTransport, setSearchTermTransport] = useState("");
  const [showDialogTransport, setShowDialogTransport] = useState(false);
  const [editingTransport, setEditingTransport] =
    useState<TransportBuffer | null>(null);
  const [deleteConfirmTransport, setDeleteConfirmTransport] = useState<
    string | null
  >(null);
  const [sortFieldTransport, setSortFieldTransport] =
    useState<SortFieldTransport | null>(null);
  const [sortOrderTransport, setSortOrderTransport] = useState<SortOrder>(null);

  const [venuesList, setVenuesList] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [portsList, setPortsList] = useState<{ id: string; name: string }[]>(
    [],
  );

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const { screenName } = useScreenPermission();
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: screenName,
      childTitle: "Configure time buffers for accommodation check-in/check-out and transport scheduling",
      icon: <Clock className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader, screenName]);

  // Accommodation form data
  const [accommodationFormData, setAccommodationFormData] = useState({
    venue: "",
    checkInBuffer: "00:00",
    checkOutBuffer: "00:00",
    description: "",
    isActive: true,
  });

  // Transport form data
  const [transportFormData, setTransportFormData] = useState({
    venue: "",
    port: "",
    preFlightBuffer: "00:00",
    postFlightBuffer: "00:00",
    description: "",
    isActive: true,
  });

  // Accommodation validation errors
  const [accommodationErrors, setAccommodationErrors] = useState({
    venue: "",
  });

  // Transport validation errors
  const [transportErrors, setTransportErrors] = useState({
    venue: "",
    port: "",
  });

  useEffect(() => {
    const fetchBuffers = async () => {
      try {
        setLoading(true);
        const res = await getBufferConfigurations(
          activeTab === "accommodation"
            ? BUFFER_TYPE.ACCOMMODATION
            : BUFFER_TYPE.TRANSPORT,
            pageNo,
            pageSize
        );

        setTotalCount(res.totalCount);

        if (activeTab === "accommodation") {
          setAccommodationBuffers(
            res.items.map((item) => ({
              id: item.publicId,
              venue: item.venueName || "-",
              venueId: item.venueId.toString(),
              checkInBuffer: item.checkInBuffer,
              checkOutBuffer: item.checkOutBuffer,
              description: item.description || "",
              isActive: item.isActive,
              createdAt: "",
              updatedAt: "",
            })),
          );
        } else {
          setTransportBuffers(
            res.items.map((item) => ({
              id: item.publicId,
              venue: item.venueName,
              venueId: item.venueId.toString(),
              port: item.portId?.toString() || "",
              portName: item.portName || "-",
              preFlightBuffer: item.checkInBuffer,
              postFlightBuffer: item.checkOutBuffer,
              description: item.description || "",
              isActive: item.isActive,
              createdAt: "",
              updatedAt: "",
            })),
          );
        }
      } catch {
        toast.error("Failed to load buffer configurations");
      } finally {
        setLoading(false);
      }
    };

    fetchBuffers();
  }, [activeTab, pageNo, pageSize]);

  useEffect(() => {
    setPageNo(1);
  }, [activeTab]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        setLoading(true);
        const res = await getVenues();

        const formatted = res
          .filter((v) => v.isActive)
          .map((v) => ({
            id: v.venueId.toString(),
            name: v.venueName,
          }));

        setVenuesList(formatted);
      } catch (error) {
        toast.error("Failed to load venues");
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, []);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        setLoading(true);
        const res = await getPorts();
        const formatted = res
          .filter((p) => p.isActive)
          .map((p) => ({
            id: p.portId.toString(),
            name: p.portName,
          }));

        setPortsList(formatted);
      } catch (error) {
        toast.error("Failed to load ports");
      } finally {
        setLoading(false);
      }
    };

    fetchPorts();
  }, []);

  // Accommodation handlers
  const handleOpenDialogAccommodation = (buffer?: AccommodationBuffer) => {
    if (buffer) {
      setEditingAccommodation(buffer);
      setAccommodationFormData({
        venue: buffer.venueId, 
        checkInBuffer: buffer.checkInBuffer,
        checkOutBuffer: buffer.checkOutBuffer,
        description: buffer.description,
        isActive: buffer.isActive,
      });
    } else {
      setEditingAccommodation(null);
      setAccommodationFormData({
        venue: "",
        checkInBuffer: "00:00",
        checkOutBuffer: "00:00",
        description: "",
        isActive: true,
      });
    }
    setShowDialogAccommodation(true);
  };

  const handleSaveAccommodation = async () => {
    const errors = { venue: "" };

    if (!accommodationFormData.venue) {
      errors.venue = "Venue is required";
    }

    setAccommodationErrors(errors);
    if (errors.venue) return;

    try {
      setLoading(true);
      const payload = {
        type: BUFFER_TYPE.ACCOMMODATION,
        venueId: Number(accommodationFormData.venue),
        portId: null,
        checkInBuffer: accommodationFormData.checkInBuffer,
        checkOutBuffer: accommodationFormData.checkOutBuffer,
        description: accommodationFormData.description,
        isActive: accommodationFormData.isActive,
      };

      if (editingAccommodation) {
        await updateBufferConfiguration(editingAccommodation.id, payload);
        toast.success("Accommodation buffer updated successfully");
      } else {
        await createBufferConfiguration(payload);
        toast.success("Accommodation buffer created successfully");
      }

      setShowDialogAccommodation(false);

      const res = await getBufferConfigurations(
        BUFFER_TYPE.ACCOMMODATION, 
        pageNo,
        pageSize);

        setTotalCount(res.totalCount);

      setAccommodationBuffers(
        res.items.map((item) => ({
          id: item.publicId,
          venue: item.venueName,
          venueId: item.venueId.toString(),
          checkInBuffer: item.checkInBuffer,
          checkOutBuffer: item.checkOutBuffer,
          description: item.description || "",
          isActive: item.isActive,
          createdAt: "",
          updatedAt: "",
        }))
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        "Failed to save accommodation buffer";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccommodation = async (id: string) => {
    try {
      setLoading(true);
      await deleteBufferConfiguration(id);
      toast.success("Accommodation buffer deleted successfully");

      setDeleteConfirmAccommodation(null);

      const res = await getBufferConfigurations(BUFFER_TYPE.ACCOMMODATION);

      setAccommodationBuffers(
        res.items.map((item) => ({
          id: item.publicId,
          venue: item.venueName,
          venueId: item.venueId.toString(),
          checkInBuffer: item.checkInBuffer,
          checkOutBuffer: item.checkOutBuffer,
          description: item.description || "",
          isActive: item.isActive,
          createdAt: "",
          updatedAt: "",
        }))
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        "Failed to delete accommodation buffer";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveAccommodation = async (id: string) => {
    try {
      setLoading(true);
      const buffer = accommodationBuffers.find((b) => b.id === id);
      if (!buffer) return;

      const newStatus = !buffer.isActive;
      await toggleBufferStatus(id, newStatus);

      setAccommodationBuffers((prev) =>
        prev.map((b) => (b.id === id ? { ...b, isActive: newStatus } : b)),
      );

      toast.success(
        `Buffer ${newStatus ? "activated" : "deactivated"} successfully`,
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to update status";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  // Transport handlers
  const handleOpenDialogTransport = (buffer?: TransportBuffer) => {
    if (buffer) {
      setEditingTransport(buffer);
      setTransportFormData({
        venue: buffer.venueId,
        port: buffer.port,
        preFlightBuffer: buffer.preFlightBuffer,
        postFlightBuffer: buffer.postFlightBuffer,
        description: buffer.description,
        isActive: buffer.isActive,
      });
    } else {
      setEditingTransport(null);
      setTransportFormData({
        venue: "",
        port: "",
        preFlightBuffer: "00:00",
        postFlightBuffer: "00:00",
        description: "",
        isActive: true,
      });
    }
    setShowDialogTransport(true);
  };

  const handleSaveTransport = async () => {
    const errors = { venue: "", port: "" };

    if (!transportFormData.venue) errors.venue = "Venue is required";
    if (!transportFormData.port) errors.port = "Port is required";

    setTransportErrors(errors);
    if (errors.venue || errors.port) return;

    try {
      setLoading(true);
      const payload = {
        type: BUFFER_TYPE.TRANSPORT,
        venueId: Number(transportFormData.venue),
        portId: Number(transportFormData.port),
        checkInBuffer: transportFormData.preFlightBuffer,
        checkOutBuffer: transportFormData.postFlightBuffer,
        description: transportFormData.description,
        isActive: transportFormData.isActive,
      };

      if (editingTransport) {
        await updateBufferConfiguration(editingTransport.id, payload);
        toast.success("Transport buffer updated successfully");
      } else {
        await createBufferConfiguration(payload);
        toast.success("Transport buffer created successfully");
      }

      setShowDialogTransport(false);

      const res = await getBufferConfigurations(BUFFER_TYPE.TRANSPORT);

      setTransportBuffers(
        res.items.map((item) => ({
          id: item.publicId,
          venue: item.venueName,
          venueId: item.venueId.toString(),
          port: item.portId?.toString() || "",
          portName: item.portName || "-",
          preFlightBuffer: item.checkInBuffer,
          postFlightBuffer: item.checkOutBuffer,
          description: item.description || "",
          isActive: item.isActive,
          createdAt: "",
          updatedAt: "",
        }))
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        "Failed to save transport buffer";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransport = async (id: string) => {
    try {
      setLoading(true);
      await deleteBufferConfiguration(id);

      toast.success("Transport buffer deleted successfully");

      setDeleteConfirmTransport(null);

      const res = await getBufferConfigurations(BUFFER_TYPE.TRANSPORT);

      setTransportBuffers(
        res.items.map((item) => ({
          id: item.publicId,
          venue: item.venueName,
          venueId: item.venueId.toString(),
          port: item.portId?.toString() || "",
          portName: item.portName || "-",
          preFlightBuffer: item.checkInBuffer,
          postFlightBuffer: item.checkOutBuffer,
          description: item.description || "",
          isActive: item.isActive,
          createdAt: "",
          updatedAt: "",
        }))
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        "Failed to delete transport buffer";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveTransport = async (id: string) => {
    try {
      setLoading(true);
      const buffer = transportBuffers.find((b) => b.id === id);
      if (!buffer) return;

      const newStatus = !buffer.isActive;
      await toggleBufferStatus(id, newStatus);
      setTransportBuffers((prev) =>
        prev.map((b) => (b.id === id ? { ...b, isActive: newStatus } : b)),
      );

      toast.success(
        `Buffer ${newStatus ? "activated" : "deactivated"} successfully`,
      );
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  // Sorting handlers
  const handleSortAccommodation = (field: SortFieldAccommodation) => {
    if (sortFieldAccommodation === field) {
      if (sortOrderAccommodation === "asc") {
        setSortOrderAccommodation("desc");
      } else if (sortOrderAccommodation === "desc") {
        setSortFieldAccommodation(null);
        setSortOrderAccommodation(null);
      }
    } else {
      setSortFieldAccommodation(field);
      setSortOrderAccommodation("asc");
    }
  };

  const handleSortTransport = (field: SortFieldTransport) => {
    if (sortFieldTransport === field) {
      if (sortOrderTransport === "asc") {
        setSortOrderTransport("desc");
      } else if (sortOrderTransport === "desc") {
        setSortFieldTransport(null);
        setSortOrderTransport(null);
      }
    } else {
      setSortFieldTransport(field);
      setSortOrderTransport("asc");
    }
  };

  // Sortable Header Component
  const SortableHeaderAccommodation = ({
    column,
    label,
  }: {
    column: SortFieldAccommodation;
    label: string;
  }) => {
    const isActive = sortFieldAccommodation === column;
    return (
      <TableHead
        style={{ color: "#5B9BD5" }}
        className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
        onClick={() => handleSortAccommodation(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortOrderAccommodation === "asc" ? (
              <ArrowUp className="w-3 h-3" />
            ) : (
              <ArrowDown className="w-3 h-3" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 opacity-30" />
          )}
        </div>
      </TableHead>
    );
  };

  const SortableHeaderTransport = ({
    column,
    label,
  }: {
    column: SortFieldTransport;
    label: string;
  }) => {
    const isActive = sortFieldTransport === column;
    return (
      <TableHead
        style={{ color: "#5B9BD5" }}
        className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
        onClick={() => handleSortTransport(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortOrderTransport === "asc" ? (
              <ArrowUp className="w-3 h-3" />
            ) : (
              <ArrowDown className="w-3 h-3" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 opacity-30" />
          )}
        </div>
      </TableHead>
    );
  };

  // Apply sorting for accommodation
  const applySortingAccommodation = (buffers: AccommodationBuffer[]) => {
    if (!sortFieldAccommodation || !sortOrderAccommodation) return buffers;

    return [...buffers].sort((a, b) => {
      const aValue = a[sortFieldAccommodation as SortFieldAccommodation];
      const bValue = b[sortFieldAccommodation as SortFieldAccommodation];

      if (aValue < bValue) return sortOrderAccommodation === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrderAccommodation === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Apply sorting for transport
  const applySortingTransport = (buffers: TransportBuffer[]) => {
    if (!sortFieldTransport || !sortOrderTransport) return buffers;

    return [...buffers].sort((a, b) => {
      const aValue = a[sortFieldTransport as SortFieldTransport];
      const bValue = b[sortFieldTransport as SortFieldTransport];

      if (aValue < bValue) return sortOrderTransport === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrderTransport === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Filter and sort accommodation buffers
  const processedAccommodationBuffers = applySortingAccommodation(
    accommodationBuffers.filter((buffer) => {
      const search = searchTermAccommodation.trim().toLowerCase();

      if (!search) return true;

      return (
        buffer.venue?.toLowerCase().includes(search) ||
        buffer.description?.toLowerCase().includes(search)
      );
    }),
  );

  // Filter and sort transport buffers
  const processedTransportBuffers = applySortingTransport(
    transportBuffers.filter((buffer) => {
      const search = searchTermTransport.trim().toLowerCase();

      return (
        (buffer.venue || "").toLowerCase().includes(search) ||
        (buffer.portName || "").toLowerCase().includes(search) ||
        (buffer.description || "").toLowerCase().includes(search)
      );
    }),
  );

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-4 sm:space-y-6 pb-8 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">

          <Button
            onClick={() =>
              activeTab === "accommodation"
                ? handleOpenDialogAccommodation()
                : handleOpenDialogTransport()
            }
            style={{ background: "#5B9BD5" }}
            className="text-white text-sm sm:text-base whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Buffer
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger
              value="accommodation"
              className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white"
            >
              <Hotel className="w-4 h-4 mr-2" />
              Accommodation Buffer
            </TabsTrigger>
            <TabsTrigger
              value="transport"
              className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white"
            >
              <Plane className="w-4 h-4 mr-2" />
              Transport Buffer
            </TabsTrigger>
          </TabsList>

          {/* Accommodation Tab */}
          <TabsContent value="accommodation" className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <Input
                placeholder="Search by venue, description..."
                value={searchTermAccommodation}
                onChange={(e) => setSearchTermAccommodation(e.target.value)}
                className="pl-9 sm:pl-10 text-sm sm:text-base h-9 sm:h-10 w-full"
              />
            </div>

            {/* Table */}
            <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
              <CardHeader
                className="border-b bg-gradient-to-r from-sky-50 to-blue-50 px-4 sm:px-6 py-3 sm:py-4"
                style={{ gap: "0px" }}
              >
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="text-base sm:text-lg"
                >
                  Accommodation Buffer Configurations
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {processedAccommodationBuffers.length} configuration
                  {processedAccommodationBuffers.length !== 1 ? "s" : ""} found
                </CardDescription>
              </CardHeader>
              <CardContent style={{ padding: "0px 0px 10px 0px" }}>
                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                        <SortableHeaderAccommodation
                          column="venue"
                          label="Venue"
                        />
                        <SortableHeaderAccommodation
                          column="checkInBuffer"
                          label="Check-In"
                        />
                        <SortableHeaderAccommodation
                          column="checkOutBuffer"
                          label="Check-Out"
                        />
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs"
                        >
                          Description
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs"
                        >
                          Status
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs text-right"
                        >
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {processedAccommodationBuffers.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No accommodation buffer configurations found
                          </TableCell>
                        </TableRow>
                      ) : (
                        processedAccommodationBuffers.map((buffer) => (
                          <TableRow
                            key={buffer.id}
                            className="hover:bg-sky-50/30"
                          >
                            <TableCell className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#5B9BD5]" />
                                <span className="font-medium">
                                  {buffer.venue}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                {buffer.checkInBuffer}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                {buffer.checkOutBuffer}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-4 py-2 max-w-xs">
                              <span className="text-sm text-gray-600 line-clamp-2">
                                {buffer.description}
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <Switch
                                checked={buffer.isActive}
                                onCheckedChange={() =>
                                  toggleActiveAccommodation(buffer.id)
                                }
                              />
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleOpenDialogAccommodation(buffer)
                                  }
                                  className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setDeleteConfirmAccommodation(buffer.id)
                                  }
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden divide-y">
                  {processedAccommodationBuffers.length === 0 ? (
                    <div className="text-center py-8 px-4 text-gray-500 text-sm">
                      No accommodation buffer configurations found
                    </div>
                  ) : (
                    processedAccommodationBuffers.map((buffer) => (
                      <div
                        key={buffer.id}
                        className="p-4 space-y-3 hover:bg-sky-50/30"
                      >
                        {/* Header */}
                        <div className="flex items-start justify-end gap-2">
                          <div className="flex items-center gap-2 flex-1">
                            <MapPin className="w-5 h-5 text-[#5B9BD5] flex-shrink-0" />
                            <div>
                              <div className="font-medium text-sm">
                                {buffer.venue}
                              </div>
                            </div>
                          </div>
                          <Badge
                            className={
                              buffer.isActive
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }
                          >
                            {buffer.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        {/* Buffer Info */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="text-xs font-medium text-green-700 mb-1">
                              Check-In Buffer
                            </div>
                            <div className="text-sm font-semibold text-[#5B9BD5]">
                              {buffer.checkInBuffer}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Before arrival
                            </div>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="text-xs font-medium text-blue-700 mb-1">
                              Check-Out Buffer
                            </div>
                            <div className="text-sm font-semibold text-[#5B9BD5]">
                              {buffer.checkOutBuffer}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              After departure
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        {buffer.description && (
                          <p className="text-xs text-gray-600 border-t pt-2">
                            {buffer.description}
                          </p>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between gap-2 border-t pt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Status:</span>
                            <Switch
                              checked={buffer.isActive}
                              onCheckedChange={() =>
                                toggleActiveAccommodation(buffer.id)
                              }
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleOpenDialogAccommodation(buffer)
                              }
                              className="text-[#5B9BD5] border-[#5B9BD5]/30"
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setDeleteConfirmAccommodation(buffer.id)
                              }
                              className="text-red-600 border-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
              {totalCount > pageSize && (
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  currentPage={pageNo}
                  onPageChange={(page) => setPageNo(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNo(1);
                  }}
                  label="properties"
                />
              )}
            </Card>
          </TabsContent>

          {/* Transport Tab */}
          <TabsContent value="transport" className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <Input
                placeholder="Search by venue..."
                value={searchTermTransport}
                onChange={(e) => setSearchTermTransport(e.target.value)}
                className="pl-9 sm:pl-10 text-sm sm:text-base h-9 sm:h-10 w-full"
              />
            </div>

            {/* Table */}
            <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
              <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="text-base sm:text-lg"
                >
                  Transport Buffer Configurations
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {processedTransportBuffers.length} configuration
                  {processedTransportBuffers.length !== 1 ? "s" : ""} found
                </CardDescription>
              </CardHeader>
              <CardContent style={{ padding: "0px 0px 10px 0px" }}>
                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                        <SortableHeaderTransport column="venue" label="Venue" />
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs"
                        >
                          Port
                        </TableHead>
                        <SortableHeaderTransport
                          column="postFlightBuffer"
                          label="Post-Travel"
                        />
                        <SortableHeaderTransport
                          column="preFlightBuffer"
                          label="Pre-Travel"
                        />
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs"
                        >
                          Status
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs text-right"
                        >
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {processedTransportBuffers.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No transport buffer configurations found
                          </TableCell>
                        </TableRow>
                      ) : (
                        processedTransportBuffers.map((buffer) => (
                          <TableRow
                            key={buffer.id}
                            className="hover:bg-sky-50/30"
                          >
                            <TableCell className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#5B9BD5]" />
                                <span className="font-medium">
                                  {buffer.venue}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <Plane className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-700">
                                  {buffer.portName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                                {buffer.postFlightBuffer}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                {buffer.preFlightBuffer}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <Switch
                                checked={buffer.isActive}
                                onCheckedChange={() =>
                                  toggleActiveTransport(buffer.id)
                                }
                              />
                            </TableCell>
                            <TableCell className="px-4 py-2">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleOpenDialogTransport(buffer)
                                  }
                                  className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setDeleteConfirmTransport(buffer.id)
                                  }
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden divide-y">
                  {processedTransportBuffers.length === 0 ? (
                    <div className="text-center py-8 px-4 text-gray-500 text-sm">
                      No transport buffer configurations found
                    </div>
                  ) : (
                    processedTransportBuffers.map((buffer) => (
                      <div
                        key={buffer.id}
                        className="p-4 space-y-3 hover:bg-sky-50/30"
                      >
                        {/* Header */}
                        <div className="flex items-start justify-end gap-2">
                          <div className="flex items-center gap-2 flex-1">
                            <MapPin className="w-5 h-5 text-[#5B9BD5] flex-shrink-0" />
                            <div>
                              <div className="font-medium text-sm">
                                {buffer.venue}
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                <Plane className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-600">
                                  {buffer.portName}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Badge
                            className={
                              buffer.isActive
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }
                          >
                            {buffer.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        {/* Buffer Info */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="text-xs font-medium text-purple-700 mb-1">
                              Post-Travel Buffer
                            </div>
                            <div className="text-sm font-semibold text-[#5B9BD5]">
                              {buffer.postFlightBuffer}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              After arrival
                            </div>
                          </div>

                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <div className="text-xs font-medium text-orange-700 mb-1">
                              Pre-Travel Buffer
                            </div>
                            <div className="text-sm font-semibold text-[#5B9BD5]">
                              {buffer.preFlightBuffer}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Before departure
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between gap-2 border-t pt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Status:</span>
                            <Switch
                              checked={buffer.isActive}
                              onCheckedChange={() =>
                                toggleActiveTransport(buffer.id)
                              }
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenDialogTransport(buffer)}
                              className="text-[#5B9BD5] border-[#5B9BD5]/30"
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirmTransport(buffer.id)}
                              className="text-red-600 border-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
              {totalCount > pageSize && (
              <Pagination
                totalItems={totalCount}
                itemsPerPage={pageSize}
                currentPage={pageNo}
                onPageChange={(page) => setPageNo(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPageNo(1);
                }}
                label="properties"
              />
            )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Accommodation Add/Edit Dialog */}
        <Dialog
          open={showDialogAccommodation}
          onOpenChange={setShowDialogAccommodation}
        >
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle
                style={{ color: "#5B9BD5", gap: "2px" }}
                className="flex items-center"
              >
                <Hotel className="w-5 h-5" />
                {editingAccommodation
                  ? "Edit Accommodation Buffer"
                  : "Add Accommodation Buffer"}
              </DialogTitle>
              <DialogDescription>
                {editingAccommodation
                  ? "Update the accommodation buffer configuration"
                  : "Create a new accommodation buffer configuration for a venue"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Venue */}
              <div className="space-y-2">
                <SelectFieldset label="Venue" required>
                  <Select
                    value={accommodationFormData.venue}
                    onValueChange={(value) => {
                      setAccommodationFormData({
                        ...accommodationFormData,
                        venue: value,
                      });
                      setAccommodationErrors({ ...accommodationErrors, venue: "" });
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue placeholder="Select venue" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {venuesList.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id}>
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {accommodationErrors.venue && (
                  <p className="text-red-500 text-xs mt-1">
                    {accommodationErrors.venue}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Select the venue where this buffer will apply
                </p>
              </div>

              {/* Buffer Hours Grid */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-2">
                  <Clock className="w-5 h-5 text-[#5B9BD5]" />
                  <h3 className="font-semibold text-[#5B9BD5]">
                    Buffer Hours Configuration
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Check-In Buffer */}
                  <div className="space-y-2 bg-green-50 border border-green-200 rounded-lg p-4">
                    <Label
                      htmlFor="acc-checkin"
                      className="text-green-700 font-medium"
                    >
                      Check-In Buffer (hours)
                    </Label>
                    <TimePickerField
                      value={accommodationFormData.checkInBuffer}
                      placeholder="02:30"
                      onChange={(val) =>
                        setAccommodationFormData({
                          ...accommodationFormData,
                          checkInBuffer: val,
                        })
                      }
                    />
                    <p className="text-xs text-green-700">
                      Hours <strong>after</strong> reaching the venue for check-in
                      time calculation
                    </p>
                  </div>

                  {/* Check-Out Buffer */}
                  <div className="space-y-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label
                      htmlFor="acc-checkout"
                      className="text-blue-700 font-medium"
                    >
                      Check-Out Buffer (hours)
                    </Label>
                    <TimePickerField
                      value={accommodationFormData.checkOutBuffer}
                      placeholder="03:00"
                      onChange={(val) =>
                        setAccommodationFormData({
                          ...accommodationFormData,
                          checkOutBuffer: val,
                        })
                      }
                    />
                    <p className="text-xs text-blue-700">
                      Hours <strong>before</strong> travel time after check-out
                      time calculation
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <SelectFieldset label="Description">
                  <TextareaUI
                    id="acc-description"
                    value={accommodationFormData.description}
                    onChange={(e) =>
                      setAccommodationFormData({
                        ...accommodationFormData,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter a brief description for this buffer configuration..."
                    rows={3}
                  />
                </SelectFieldset>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div>
                  <Label htmlFor="acc-active" className="text-base font-medium">
                    Active Status
                  </Label>
                  <p className="text-sm text-gray-600 mt-1">
                    Enable this configuration to use it in accommodation
                    scheduling
                  </p>
                </div>
                <Switch
                  id="acc-active"
                  checked={accommodationFormData.isActive}
                  onCheckedChange={(checked) =>
                    setAccommodationFormData({
                      ...accommodationFormData,
                      isActive: checked,
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDialogAccommodation(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAccommodation}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                {editingAccommodation
                  ? "Update Configuration"
                  : "Create Configuration"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transport Add/Edit Dialog */}
        <Dialog open={showDialogTransport} onOpenChange={setShowDialogTransport}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle
                style={{ color: "#5B9BD5" }}
                className="flex items-center gap-2"
              >
                <Plane className="w-5 h-5" />
                {editingTransport
                  ? "Edit Transport Buffer"
                  : "Add Transport Buffer"}
              </DialogTitle>
              <DialogDescription>
                {editingTransport
                  ? "Update the transport buffer configuration"
                  : "Create a new transport buffer configuration for a venue"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Venue and Port in single line */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Venue */}
                <div className="space-y-2">
                  <Label htmlFor="trans-venue">
                    Venue <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={transportFormData.venue}
                    onValueChange={(value) => {
                      setTransportFormData({
                        ...transportFormData,
                        venue: value,
                      });
                      setTransportErrors({ ...transportErrors, venue: "" });
                    }}
                  >
                    <SelectTrigger
                      id="trans-venue"
                      className={
                        transportErrors.venue
                          ? "border-red-500 focus:ring-red-500"
                          : ""
                      }
                    >
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {venuesList.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id}>
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {transportErrors.venue && (
                    <p className="text-red-500 text-xs mt-1">
                      {transportErrors.venue}
                    </p>
                  )}
                </div>

                {/* Port */}
                <div className="space-y-2">
                  <Label htmlFor="trans-port">
                    Port <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={transportFormData.port}
                    onValueChange={(value) => {
                      setTransportFormData({ ...transportFormData, port: value });
                      setTransportErrors({ ...transportErrors, port: "" });
                    }}
                  >
                    <SelectTrigger
                      id="trans-port"
                      className={
                        transportErrors.port
                          ? "border-red-500 focus:ring-red-500"
                          : ""
                      }
                    >
                      <SelectValue placeholder="Select port" />
                    </SelectTrigger>
                    <SelectContent>
                      {portsList.map((port) => (
                        <SelectItem key={port.id} value={port.id}>
                          {port.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {transportErrors.port && (
                    <p className="text-red-500 text-xs mt-1">
                      {transportErrors.port}
                    </p>
                  )}
                </div>
              </div>

              {/* Buffer Hours Grid */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-2">
                  <Clock className="w-5 h-5 text-[#5B9BD5]" />
                  <h3 className="font-semibold text-[#5B9BD5]">
                    Buffer Hours Configuration
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Post-Travel Buffer - Now First */}
                  <div className="space-y-2 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <Label
                      htmlFor="trans-postflight"
                      className="text-purple-700 font-medium"
                    >
                      Post-Travel Buffer (hours)
                    </Label>

                    <TimePickerField
                      value={transportFormData.postFlightBuffer}
                      placeholder="03:00"
                      onChange={(val) =>
                        setTransportFormData({
                          ...transportFormData,
                          postFlightBuffer: val,
                        })
                      }
                    />
                    <p className="text-xs text-purple-700">
                      Hours <strong>after</strong> flight arrival for travel time
                    </p>
                  </div>

                  {/* Pre-Travel Buffer */}
                  <div className="space-y-2 bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <Label
                      htmlFor="trans-preflight"
                      className="text-orange-700 font-medium"
                    >
                      Pre-Travel Buffer (hours)
                    </Label>
                    <TimePickerField
                      value={transportFormData.preFlightBuffer}
                      placeholder="03:00"
                      onChange={(val) =>
                        setTransportFormData({
                          ...transportFormData,
                          preFlightBuffer: val,
                        })
                      }
                    />
                    <p className="text-xs text-orange-700">
                      Hours <strong>before</strong> flight departure for travel
                      time
                    </p>
                  </div>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div>
                  <Label htmlFor="trans-active" className="text-base font-medium">
                    Active Status
                  </Label>
                  <p className="text-sm text-gray-600 mt-1">
                    Enable this configuration to use it in transport scheduling
                  </p>
                </div>
                <Switch
                  id="trans-active"
                  checked={transportFormData.isActive}
                  onCheckedChange={(checked) =>
                    setTransportFormData({
                      ...transportFormData,
                      isActive: checked,
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDialogTransport(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTransport}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                {editingTransport
                  ? "Update Configuration"
                  : "Create Configuration"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Accommodation Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmAccommodation !== null}
          onOpenChange={() => setDeleteConfirmAccommodation(null)}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Accommodation Buffer
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this accommodation buffer
                configuration? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Deleting this configuration will affect
                accommodation scheduling for this venue.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmAccommodation(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  deleteConfirmAccommodation &&
                  handleDeleteAccommodation(deleteConfirmAccommodation)
                }
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transport Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmTransport !== null}
          onOpenChange={() => setDeleteConfirmTransport(null)}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Transport Buffer
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this transport buffer
                configuration? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Deleting this configuration will affect
                transport scheduling for this venue.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmTransport(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  deleteConfirmTransport &&
                  handleDeleteTransport(deleteConfirmTransport)
                }
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
